import { Injectable } from "@angular/core";
import { throwError as observableThrowError, Observable } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { CashRegisterEntity } from "../../models/entity/cash-register/cash-register-entity";
import { BaseRestService } from "../base-rest.service";
import { SessionStorageService } from "../session-storage.service";
import { RequestCacheService } from "../core/request-cache.service";
import { CashRegisterXReportLocalRequest } from '../../models/rest/cash-register/report/x-report/cash-register-x-report-local-request';
import { CashRegisterXReportLocalResponse } from '../../models/rest/cash-register/report/x-report/cash-register-x-report-local-response';
import { CashRegisterXReportVendorHandleRequest } from '../../models/rest/cash-register/report/x-report/cash-register-x-report-vendor-handle-request';
import { CashRegisterXReportVendorHandleResponse } from '../../models/rest/cash-register/report/x-report/cash-register-x-report-vendor-handle-response';
import { CashRegisterZReportBulkLocalResponse } from '../../models/rest/cash-register/report/z-report/cash-register-z-report-bulk-local-response';
import { CashRegisterZReportBulkLocalRequest } from '../../models/rest/cash-register/report/z-report/cash-register-z-report-bulk-local-request';
import { CashRegisterZReportBulkVendorHandleRequest } from '../../models/rest/cash-register/report/z-report/cash-register-z-report-bulk-vendor-handle-request';
import { CashRegisterZReportBulkVendorHandleResponse } from '../../models/rest/cash-register/report/z-report/cash-register-z-report-bulk-vendor-handle-response';
import { CashRegisterCloseShiftLocalResponse } from '../../models/rest/cash-register/close_shift/cash-register-close-shift-local-response';
import { CashRegisterCloseShiftLocalRequest } from '../../models/rest/cash-register/close_shift/cash-register-close-shift-local-request';
import { CashRegisterCloseShiftVendorHandleRequest } from '../../models/rest/cash-register/close_shift/cash-register-close-shift-vendor-handle-request';
import { CashRegisterCloseShiftVendorHandleResponse } from '../../models/rest/cash-register/close_shift/cash-register-close-shift-vendor-handle-response';
import { CashRegisterOpenShiftVendorHandleRequest } from '../../models/rest/cash-register/open_shift/cash-register-open-shift-vendor-handle-request';
import { CashRegisterOpenShiftVendorHandleResponse } from '../../models/rest/cash-register/open_shift/cash-register-open-shift-vendor-handle-response';
import { CashRegisterOpenShiftLocalResponse } from '../../models/rest/cash-register/open_shift/cash-register-open-shift-local-response';
import { CashRegisterOpenShiftLocalRequest } from '../../models/rest/cash-register/open_shift/cash-register-open-shift-local-request';
import { CashRegisterLoginLocalRequest } from '../../models/rest/cash-register/login/cash-register-login-local-request';
import { CashRegisterLoginLocalResponse } from '../../models/rest/cash-register/login/cash-register-login-local-response';
import { CashRegisterLoginVendorHandleRequest } from '../../models/rest/cash-register/login/cash-register-login-vendor-handle-request';
import { CashRegisterLoginVendorHandleResponse } from '../../models/rest/cash-register/login/cash-register-login-vendor-handle-response';
import { CashRegisterControlTapeLocalRequest } from '../../models/rest/cash-register/control-tape/cash-register-control-tape-local-request';
import { CashRegisterControlTapeLocalResponse } from '../../models/rest/cash-register/control-tape/cash-register-control-tape-local-response';
import { CashRegisterControlTapeVendorHandleRequest } from '../../models/rest/cash-register/control-tape/cash-register-control-tape-vendor-handle-request';
import { CashRegisterControlTapeVendorHandleResponse } from '../../models/rest/cash-register/control-tape/cash-register-control-tape-vendor-handle-response';
import { CashRegisterPairVendorHandleRequest } from '../../models/rest/cash-register/pair/cash-register-pair-vendor-handle-request';
import { CashRegisterPairVendorHandleResponse } from '../../models/rest/cash-register/pair/cash-register-pair-vendor-handle-response';
import { CashRegisterPairLocalRequest } from '../../models/rest/cash-register/pair/cash-register-pair-local-request';
import { CashRegisterPairLocalResponse } from '../../models/rest/cash-register/pair/cash-register-pair-local-response';


@Injectable({
  providedIn: 'root'
})
export class CashRegisterService extends BaseRestService {
  constructor(protected httpClient: HttpClient, protected sessionStorageService: SessionStorageService,
    private cache: RequestCacheService) {
    super(httpClient, sessionStorageService);
  }


  getCashRegisters(): Observable<CashRegisterEntity[]> {
    const url = `/cash-registers`;
    return this.httpGet<CashRegisterEntity[]>(url).pipe(catchError(this.errorHandler));
  }

  getCashRegisterById(id): Observable<CashRegisterEntity> {
    const url = `/cash-registers/${id}`;
    return this.httpGet<CashRegisterEntity>(url).pipe(catchError(this.errorHandler));
  }

  addCashRegister(payment) {
    const url = `/cash-registers`;
    return this.httpPost(url, payment).pipe(
      tap(() => {
        // Topluluk cache'ini temizle
        this.cache.clearByKeyPattern("cash-registers");
      }),
      catchError(this.errorHandler));
  }

  updateCashRegister(id, data): Observable<CashRegisterEntity> {
    const url = `/cash-registers/${id}`;
    return this.httpPut<CashRegisterEntity>(url, data).pipe(tap(() => {
      // Topluluk cache'ini temizle
      this.cache.clearByKeyPattern("cash-registers");
    }),
      catchError(this.errorHandler));
  }


  deleteCashRegister(id) {
    const url = `/cash-registers/${id}`;
    return this.httpDelete(url).pipe(tap(() => {
      // Topluluk cache'ini temizle
      this.cache.clearByKeyPattern("cash-registers");
    }),
      catchError(this.errorHandler));
  }

  getXReportRequestLocal(search: CashRegisterXReportLocalRequest): Observable<CashRegisterXReportLocalResponse> {
    const url = `/cash-registers/x-report-local`;
    return this.httpPost<CashRegisterXReportLocalResponse>(url, search).pipe(catchError(this.errorHandler));
  }

  handleXReportReponse(search: CashRegisterXReportVendorHandleRequest): Observable<CashRegisterXReportVendorHandleResponse> {
    const url = `/cash-registers/handle-x-report-local`;
    return this.httpPost<CashRegisterXReportVendorHandleResponse>(url, search).pipe(catchError(this.errorHandler));
  }

  getZReportBulkRequestLocal(search: CashRegisterZReportBulkLocalRequest): Observable<CashRegisterZReportBulkLocalResponse> {
    const url = `/cash-registers/z-report-bulk-local`;
    return this.httpPost<CashRegisterZReportBulkLocalResponse>(url, search).pipe(catchError(this.errorHandler));
  }

  handleZReportBulkReponse(search: CashRegisterZReportBulkVendorHandleRequest): Observable<CashRegisterZReportBulkVendorHandleResponse> {
    const url = `/cash-registers/handle-z-report-bulk-local`;
    return this.httpPost<CashRegisterZReportBulkVendorHandleResponse>(url, search).pipe(catchError(this.errorHandler));
  }

  getOpenShiftRequestLocal(search: CashRegisterOpenShiftLocalRequest): Observable<CashRegisterOpenShiftLocalResponse> {
    const url = `/cash-registers/open-shift-local`;
    return this.httpPost<CashRegisterOpenShiftLocalResponse>(url, search).pipe(catchError(this.errorHandler));
  }

  handleOpenShiftReponse(search: CashRegisterOpenShiftVendorHandleRequest): Observable<CashRegisterOpenShiftVendorHandleResponse> {
    const url = `/cash-registers/handle-open-shift-local`;
    return this.httpPost<CashRegisterOpenShiftVendorHandleResponse>(url, search).pipe(catchError(this.errorHandler));
  }

  getCloseShiftRequestLocal(search: CashRegisterCloseShiftLocalRequest): Observable<CashRegisterCloseShiftLocalResponse> {
    const url = `/cash-registers/close-shift-local`;
    return this.httpPost<CashRegisterCloseShiftLocalResponse>(url, search).pipe(catchError(this.errorHandler));
  }

  handleCloseShiftReponse(search: CashRegisterCloseShiftVendorHandleRequest): Observable<CashRegisterCloseShiftVendorHandleResponse> {
    const url = `/cash-registers/handle-close-shift-local`;
    return this.httpPost<CashRegisterCloseShiftVendorHandleResponse>(url, search).pipe(catchError(this.errorHandler));
  }

  getLoginRequestLocal(search: CashRegisterLoginLocalRequest): Observable<CashRegisterLoginLocalResponse> {
    const url = `/cash-registers/login-local`;
    return this.httpPost<CashRegisterLoginLocalResponse>(url, search).pipe(catchError(this.errorHandler));
  }

  handleLoginReponse(search: CashRegisterLoginVendorHandleRequest): Observable<CashRegisterLoginVendorHandleResponse> {
    const url = `/cash-registers/handle-login-local`;
    return this.httpPost<CashRegisterLoginVendorHandleResponse>(url, search).pipe(catchError(this.errorHandler));
  }

  getControlTapeRequestLocal(search: CashRegisterControlTapeLocalRequest): Observable<CashRegisterControlTapeLocalResponse> {
    const url = `/cash-registers/control-tape-local`;
    return this.httpPost<CashRegisterControlTapeLocalResponse>(url, search).pipe(catchError(this.errorHandler));
  }

  handleControlTapeReponse(search: CashRegisterControlTapeVendorHandleRequest): Observable<CashRegisterControlTapeVendorHandleResponse> {
    const url = `/cash-registers/handle-control-tape-local`;
    return this.httpPost<CashRegisterControlTapeVendorHandleResponse>(url, search).pipe(catchError(this.errorHandler));
  }

  getPairRequestLocal(search: CashRegisterPairLocalRequest): Observable<CashRegisterPairLocalResponse> {
    const url = `/cash-registers/pair-local`;
    return this.httpPost<CashRegisterPairLocalResponse>(url, search).pipe(catchError(this.errorHandler));
  }

  handlePairReponse(search: CashRegisterPairVendorHandleRequest): Observable<CashRegisterPairVendorHandleResponse> {
    const url = `/cash-registers/handle-pair-local`;
    return this.httpPost<CashRegisterPairVendorHandleResponse>(url, search).pipe(catchError(this.errorHandler));
  }

  errorHandler(error: HttpErrorResponse) {
    //console.log(error);
    return observableThrowError(error);
  }
}
